// UI MESSAGE CATALOGUE — English source, bundled. Other locales come from the DATABASE (CCT-284).
//
// WHERE THE STRINGS LIVE
// ----------------------
//   * STORY CONTENT — lines, narration, card bodies. Authored and translated in the back-office,
//     stored per locale, served by the runtime. Unchanged.
//   * INTERFACE LABELS — this file for ENGLISH, `bo_translation` for every other locale.
//
// ⚠️ THIS FILE'S PREVIOUS HEADER ARGUED AGAINST PUTTING LABELS IN THE DATABASE, AND THAT
// DECISION WAS REVERSED ON PURPOSE. The objection was real and is restated here rather than
// deleted, because the design ANSWERS it: a network dependency at first paint could render an
// app with no labels at all. The answer is that English is BUNDLED HERE and is what boots.
// Nothing is fetched to draw the first screen; a dictionary is downloaded only when someone asks
// for another language, and a failed download leaves English standing.
//
// What the reversal buys: a wrong label is corrected without a release, and a fifth language
// costs an INSERT instead of a deploy.
//
// ⚠️ ENGLISH IS THE SOURCE, AND THE TYPE. `MessageKey` is derived from this object, so a key used
// by a screen but absent here is a COMPILE error. The database may add VALUES, never KEYS — a key
// that exists only in the database is invisible to the type system and to every reader.
//
// ⚠️ ADDING A LABEL IS NOT DONE UNTIL ALL FIVE LOCALES EXIST IN THE DATABASE. Adding it here
// alone means Spanish, Italian and Arabic speakers silently read English. The seed that carries
// the five is `cc-attack-bo/app/business/app_i18n_seed.py`, and `e2e/i18n.test.mjs` fails when
// the two drift apart.

export const en = {
  // — Home / catalogue
  'home.title': 'Missions',
  'home.subtitle': 'Discover new mission opportunities',
  'home.signIn': 'Sign in',
  'home.tab.active': 'Active missions ({count})',
  'home.tab.trial': 'Trial ({count})',
  'home.tab.company': 'Company ({count})',
  'home.tab.marketplace': 'Marketplace ({count})',
  'home.results': '{count} result(s) found',
  'home.results.scroll': ' Scroll to see the cards.',
  'home.results.ratio': '{shown} result(s) / {total}',
  'home.loading': 'Preparing the mission catalogue…',
  'home.empty.filtered': '{count} mission(s) hidden by the “{language}” filter.',
  'home.empty.showAll': 'Show every language',
  'home.empty.noActive': 'No active mission.',
  'home.empty.noTrial': 'No trial mission.',
  'home.empty.noCompany': 'No company mission.',
  'home.empty.noResult': 'No result for this filter.',
  'home.empty.reset': 'Reset',
  'home.filters.title': 'Filter by Tags',
  'home.language.title': 'Language',
  'home.language.all': 'All',

  // — Mission card
  'card.details': 'More details',
  'card.startTrial': 'Start Trial',
  'card.inTrial': 'In Trial',
  'card.buy': 'Buy Now',
  'card.inCart': 'In Cart',
  'card.continue': 'Continue',
  'card.open': 'Open',
  'card.enrolled': 'Enrolled · {status}',
  'card.available': 'Available',
  'card.reviews': '({count} reviews)',
  'card.progress': 'Progress',
  'card.sale': 'Sale',

  // — Quiz
  'quiz.title': 'Quiz',
  'quiz.subtitle': 'Test your knowledge',
  'quiz.chapter': 'Chapter {n}',
  'quiz.empty.title': 'No quiz in this chapter',
  'quiz.empty.body': 'The author has not written a quiz here yet. Nothing to answer, and nothing is being scored.',
  'quiz.results.title': 'Quiz results',
  'quiz.results.passed': 'You passed the quiz!',
  'quiz.results.failed': 'Not quite yet…',
  'quiz.results.retry': 'Keep practising and try again.',
  'quiz.results.score': 'Your score',
  'quiz.review.title': 'Question review',
  'quiz.singleChoice': 'Single choice (one answer only)',
  'quiz.multiChoice': 'Multiple choice (several answers)',

  // — Sign-in
  'auth.error.email': 'Please enter a valid email address.',
  'auth.error.name': 'First and last name are required.',
  'auth.error.age': 'Please enter a valid age.',
  'auth.error.country': 'Please select a country.',
  'auth.error.code': 'Please enter the code you received.',
  'auth.error.verify': 'Verification failed. Try again, or request a new code.',
  'auth.networkNotice': 'The network dropped while signing in, nothing is wrong with your account. Try again.',
  'auth.signIn': 'Sign in',
  'auth.google': 'Continue with Google',
  'auth.createAccount': 'Create an account',
  'auth.searchCountry': 'Search for a country…',
  'auth.sendCode': 'Send the code',
  'auth.error.generic': 'The operation could not be completed.',
  'me.loading': 'Loading your details…',
  'me.personalDetails': 'Personal details',
  'me.firstName': 'First name',
  'me.lastName': 'Last name',
  'me.phone': 'Phone',
  'me.age': 'Age',
  'me.selectCountry': 'Select a country',
  'me.saved': '✓ Details saved',
  // CCT-473 — account deletion, required by Google Play. The wording names what disappears AND
  // the one thing that does not: discovering a leftover after asking for deletion is what
  // destroys trust, so it is said up front rather than found later.
  'account.delete.title': 'Delete my account',
  'account.delete.explain': 'Your account, your progress and everything you submitted are erased. We act within one month.',
  'account.delete.keep': 'One thing is kept: an irreversible fingerprint of your email address, so a deleted account cannot be silently recreated. It cannot be turned back into your address.',
  'account.delete.button': 'Request deletion',
  'account.delete.confirm': 'Confirm the deletion request',
  'account.delete.cancel': 'Cancel',
  'account.delete.pending': 'Deletion requested on {date}. We act within one month.',
  'account.delete.withdraw': 'Withdraw my request',
  'account.delete.failed': 'The server could not be reached, so nothing was changed. Try again.',
  'auth.error.codeExpired': 'Wrong or expired code. Ask for a new one, then enter the latest code you received.',
  'auth.emailPrompt': 'Enter your email to receive a verification code. A new address creates an account.',
  'auth.profilePrompt': 'A few details to complete your registration.',
  'auth.field.firstName': 'First name',
  'auth.verification': 'Verification',
  'auth.verify': 'Verify',

  // — User story detail
  'us.comments.empty': 'No comment yet.',
  'us.comments.you': 'You',
  'us.comments.placeholder': 'Comment… (@ to call on a character)',
  'us.activity.loading': 'Loading the activity…',
  'us.activity.empty': 'No status change yet.',
  'us.owner': 'The owner',
  'us.owner.none': 'No owner',
  'us.action.changeStatus': 'Change status',
  'us.action.seeSolution': 'See the solution',
  'us.action.abandon': 'Give up on this task',
  'us.action.openProject': 'Open the project',

  // — Payment methods
  'payment.title': 'Payment methods',
  'payment.subtitle': 'Methods used for your purchases',
  'payment.history': 'Payment history',
  'payment.error': 'Your payment methods could not be loaded.',
  'payment.loading': 'Loading payment methods…',
  'payment.empty': 'No payment recorded.',

  // — Company workspace (admin), company detail, invitations
  'company.title': 'Company workspace',
  'company.loading': 'Loading the company workspace…',
  'company.notSignedIn': 'Not signed in.',
  'company.loadFailed': 'Could not load.',
  'company.openBackOffice': 'Open the back-office',
  'company.shortcuts': 'SHORTCUTS',
  'company.pendingInvites': '{count} invitation(s) pending',
  'company.mine': 'My companies',
  'company.none': 'No company. Create one below.',
  'company.member': 'member',
  'company.status.pendingActivation': 'Awaiting activation',
  'company.status.archived': 'Archived',
  'company.status.inactive': 'Deactivated',
  'company.status.declined': 'Declined',
  'company.status.active': 'Active',
  'company.accessAfterActivation': 'Access becomes available once an administrator activates you.',
  'company.reactivate': 'Reactivate',
  'company.archive': 'Archive',
  'company.delete': 'Delete',
  'company.deleteConfirm': 'Delete « {name} » and everything under it?\nMissions, invitations, groups and members will become unreachable. Only a global admin can restore them.',
  'company.actionFailed': 'Action failed.',
  'company.create': 'Create a company',
  'company.createNew': 'New company',
  'company.createFailed': 'Could not create.',
  'company.creating': 'Creating…',
  'company.createProvisioning': 'You will become its administrator. A tenant and a private storage space will be provisioned.',
  'company.field.name': 'Name *',
  'company.field.slug': 'slug (optional)',
  'company.field.domain': 'domain (e.g. acme.com)',
  // — Notifications
  'notif.title': 'Notifications',
  'notif.filterByType': 'Filter by type',
  'notif.enable': 'Enable',
  'notif.enablePrompt': 'Enable notifications on this device to receive pushes.',
  'notif.enabled': 'Notifications are enabled on this device.',
  'notif.iosHomeScreen': 'On iPhone and iPad, notifications require the app on the home screen. Use Share, then Add to Home Screen, and reopen CloudChrono from the icon.',
  'notif.blocked': 'Notifications are blocked for this site. Allow them from the lock icon in the address bar; private browsing blocks push.',
  'notif.refresh': 'Refresh the notifications',
  'notif.loading': 'Loading notifications…',
  'notif.loadFailed': 'Could not load the notifications.',
  'notif.empty': 'No notification',
  'notif.emptySub': 'You are all caught up.',
  'notif.justNow': 'just now',
  'notif.ago.min': '{n} min ago',
  'notif.ago.hour': '{n} h ago',
  'notif.ago.day': '{n} d ago',
  'notif.filter.all': 'All',
  'notif.filter.chat': 'Chat',
  'notif.filter.chapters': 'Chapters',
  'notif.filter.quizzes': 'Quizzes',
  'notif.filter.cards': 'Cards',
  // ⚠️ SAME MODULE-LEVEL TRAP as TABS/MEMBER_STATUS — TYPE_LABEL holds keys, resolved per render.
  'notif.type.chapter': 'Chapter',
  'notif.type.chat': 'Chat',
  'notif.type.quiz': 'Quiz',
  'notif.type.card': 'Card',
  'notif.type.mission': 'User story',
  'notif.type.badge': 'Badge',
  'notif.type.leaderboard': 'Leaderboard',
  'notif.type.system': 'System',
  'notif.type.reminder': 'Reminder',
  'notif.type.story': 'Scene',

  // — Task detail
  'task.status': 'Status',
  'task.seeMore': 'See more',
  'task.seeLess': 'See less',
  'task.activityZone': 'Comments and activity',
  'task.statusFailed': 'The status could not be changed. Reload the task, then try again.',
  'task.unknownToServer': 'The server does not know this task yet: no user story is attached to it for your mission. Its progress is therefore not tracked.',
  'task.openDiscussion': 'Open the discussion',
  'task.abandon': 'Give up on the task',
  'task.abandonConfirmTitle': 'Give up on the task?',
  'task.abandonConfirmDecisive': 'This task is decisive: giving up on it ends THE WHOLE MISSION. The task moves permanently to « Abandoned ».',
  'task.abandonConfirmNormal': 'The task moves permanently to « Abandoned ». The rest of the mission stays available.',
  'task.abandonConfirmLabel': 'Give up',
  'task.requester': 'Requester',
  'task.assignee': 'Assignee',
  'task.openProject': 'Open the project',
  'task.referenceSolution': 'Reference solution',
  'task.seeSolution': 'See the solution',
  'task.close': 'Close',
  'task.defaultValidation': 'Task validation',
  'task.codeProject': 'Code project',
  'task.codeProjectBody': 'Open the editor to carry out this user story: the project is created from the author’s template, then run from the toolbar.',
  'task.solutionBody': 'The task is finished: read the author’s solution. You can run it in the editor, but not change it.',
  'task.steps': 'Steps',
  'task.step': 'Step {n}',
  // ⚠️ THE DIFFICULTY VALUE IS A COLOUR KEY. `getDifficultyColor` compares against 'Facile' /
  // 'Moyen': translating the VALUE would silently paint every task red. Only the LABEL is
  // translated, at render time; the value itself stays untouched.
  'task.difficulty.Facile': 'Easy',
  'task.difficulty.Moyen': 'Medium',
  'task.difficulty.Difficile': 'Hard',

  // — User-story statuses. ⚠️ The MAP is exported and consumed by three files; it holds keys.
  'us.status.BACKLOG': 'Backlog',
  'us.status.ASSIGNED': 'Assigned',
  'us.status.IN_PROGRESS': 'In progress',
  'us.status.BLOCKED': 'Blocked',
  'us.status.TO_REVIEW': 'To review',
  'us.status.REVIEWED': 'Reviewed',
  'us.status.VALIDATED': 'Validated',
  'us.status.READY': 'Ready — declared complete',
  'us.status.REFUSED': 'Refused',
  'us.status.ABANDONED': 'Abandoned',
  'us.activity': 'Activity',
  'us.steps': 'Steps',
  'us.contentMissing': 'Content not found in the current scenario. This user story comes from an earlier version of the storyline.',

  // — Chapter detail
  'chapter.fallbackTitle': 'Chapter {n}',
  'chapter.steps': '{done}/{total} steps',
  // Chronomètre (CCT-288). « remaining » porte un temps déjà formaté (mm:ss) : le formatage
  // dépend de la durée, pas de la langue, et le faire ici éviterait cinq variantes à tenir.
  'chapter.timer.remaining': '{time} left',
  'chapter.timer.expired': 'Time is up',
  'chapter.timer.wholeMission': 'whole mission',
  'chapter.timer.lateRefused': 'Time ran out. This answer was not accepted.',
  'chapter.timer.missionFailed': 'The mission time limit has run out.',
  'chapter.loading': 'Loading the chapter…',
  'chapter.locked': 'Chapter locked',
  // ⚠️ LA NAVIGATION DIRECTE DOIT LE DIRE. Une URL collée contourne l'écran de lancement : la
  // partie n'a alors aucune langue figée et le moteur n'a jamais été demandé.
  'chapter.notStarted': 'This mission has not been started',
  'chapter.notStartedHint': 'You opened this chapter from a direct link. Choose the language and start the mission; the choice is frozen for the run.',
  'chapter.notEnrolledHint': 'You have no run on this mission yet. Open it from the missions list to start a trial or unlock it.',
  'chapter.startNow': 'Start the mission',
  'chapter.lockedHint': 'Finish the previous chapter to unlock this one.',
  'chapter.back': 'Back',
  'chapter.backToChapters': 'Back to the chapters',
  'chapter.close': 'Close',
  'chapter.staleDeepLink': 'The element this notification points to no longer exists; the storyline changed since it was sent.',
  'chapter.noContent': 'No content for this mission',
  'chapter.noContentBody': 'This storyline is not published in this environment. Nothing can be played or recorded, and no user story is created.',
  'chapter.action.review': 'Review',
  'chapter.action.watch': 'Watch',
  'chapter.action.openChat': 'Open the chat',
  'chapter.action.read': 'Read',
  'chapter.action.start': 'Start',

  // — Task board (kanban)
  'kanban.title': 'Tasks',
  'kanban.unavailable': 'Tasks unavailable.',
  'kanban.loading': 'Loading the board…',
  'kanban.states': 'States:',
  'kanban.decidedByEngine': 'decided by the engine',
  'kanban.closeTask': 'Close the task',
  'kanban.abandonNamedDecisive': '« {title} » is decisive: giving up on it ends THE WHOLE MISSION. The task moves permanently to « Abandoned ».',
  'kanban.abandonNamedNormal': '« {title} » moves permanently to « Abandoned ». The rest of the mission stays available.',

  // — Chat list
  'chat.none': 'No conversation for this mission.',
  'chat.noneUnlocked': 'No conversation unlocked yet.',
  'chat.tone.blocked': 'A helping hand',
  'chat.tone.refused': 'Feedback on your delivery',
  'chat.tone.validated': 'Delivery validated',
  // The player has just handed the task over (review / blocked) and said so themselves: the
  // character has not answered yet. CCT-345.
  'chat.tone.handover': 'Waiting for an answer',
  'usChat.none': 'No conversation in progress for this task.',
  'usChat.loading': 'Loading the conversation…',
  'usChat.done': 'Finished',
  'usChat.waiting': 'Waiting for the character…',
  'usChat.resume': 'Pick your task back up whenever you like.',
  'usChat.validated': 'Your delivery has been accepted. You can continue the mission.',
  'usChat.continue': 'Continue the mission',
  'usChat.backToChapter': 'Back to chapter',
  'chat.yourAnswer': 'Your answer:',
  'chat.yourAnswerPlaceholder': 'Your answer…',
  'chat.pickAnswer': 'Pick an answer:',
  'chat.pickQuestion': 'Pick a question to ask:',
  'chat.yourQuestion': 'Your question:',
  'chat.pickAnswerToContinue': 'Pick an answer to carry the conversation on:',
  'chat.pickOneOrMore': 'Select one or more answers:',
  'chat.loadFailed': 'The chat for this mission could not be loaded.',
  'charChat.loading': 'Loading the conversation…',
  // CCT-351 — the header counted with a `> 1 ? 's' : ''`, a plural rule no locale follows.
  'charChat.messageOne': '1 message',
  'charChat.messageMany': '{count} messages',
  'charChat.missionOne': '1 mission',
  'charChat.missionMany': '{count} missions',
  'charChat.empty': 'No message with this character.',
  'charChat.ctx.functional': 'On the functional side: what exactly is expected of this task?',
  'charChat.ctx.technical': 'On the technical side: where to start and where to look?',
  // Lower-case on purpose: these read INSIDE a sentence, unlike the chat-list titles above.
  'charChat.tone.blocked': 'a helping hand',
  'charChat.tone.refused': 'feedback on the delivery',
  'charChat.tone.validated': 'validation',

  // — Cart
  'cart.orderConfirmed': 'Order confirmed',
  'cart.missionsActivated': 'Your missions have been activated.',
  'cart.paymentPending': 'Payment pending',
  'cart.paymentNotFinalised': 'The payment could not be finalised. Try again.',
  'cart.error': 'Error',
  'cart.paymentFailed': 'The payment failed. Try again later.',
  'cart.itemRemoved': 'Item removed from the cart.',

  // — Shared widgets and the last few screens
  'common.retry': 'Try again',
  'common.loadFailed': 'The data could not be loaded. Check your connection.',
  'common.close': 'Close',
  // CCT-477 — draw.io diagram viewer. Zoom, layers and panning come from the viewer itself;
  // only the full-screen toggle is ours, because the viewer has none.
  'diagram.title': 'Diagram',
  'diagram.fullscreen': 'View full screen',
  // CCT-477 — the frame was blocked by the reader's own browser or network; the diagram
  // is fine, so the way out is a link rather than an empty rectangle.
  'diagram.blocked': 'This diagram could not be displayed here. Your browser or network blocked it.',
  'diagram.open': 'Open the diagram',
  'common.back': 'Back',
  'boot.connecting': 'Signing you in…',
  'boot.preparing': 'Preparing your space…',
  'boot.almost': 'Almost there…',
  'audio.mute': 'Mute the music',
  'audio.unmute': 'Unmute the music',
  'code.copy': 'Copy',
  'code.copied': 'Copied',
  'code.copyCode': 'Copy the code',
  'code.openIde': 'Open the IDE',
  'code.openIdeWithProject': 'Open the IDE with the project',
  'code.noTemplate': 'This user story has no project template.',
  'code.preparing': 'Preparing the project…',
  'profile.playerName': 'Your player name',
  'profile.pickAvatar': 'Pick your avatar',
  'invoices.loading': 'Loading invoices…',
  'invoices.empty': 'No invoice',
  'invoices.noMatch': 'No invoice matches your search.',
  'invoices.noPurchase': 'You have not made a purchase yet.',
  'thread.pickAnswerToContinue': 'Pick an answer to carry the conversation on:',
  'thread.askAbout': 'Ask about…',
  'thread.noTask': 'No task to select.',
  'mission.inCart': 'In the cart',
  'mission.openTrial': 'Open the Trial',
  'missions.companyByRole': 'Your company missions, according to your role',
  'missions.seeAllLanguages': 'See every language',
  'quiz.empty.storyBuilder': 'No question yet. This quiz is still to be completed in the Story Builder.',
  'quiz.notStarted': 'Not started',
  'quiz.status.completed': 'Passed',
  'quiz.status.failed': 'Failed',
  'quiz.status.inProgress': 'In progress',
  'missions.sub.active': 'Manage your active missions',
  'missions.sub.trial': 'Your trial missions with limited access',
  'missions.company.none': 'No company mission.',
  'missions.company.noneFor': 'No mission available for {name} (given your groups).',
  'missions.company.thisCompany': 'this company',
  'quiz.backToQuizzes': 'Back to the quizzes',
  'scene.title': 'Scene',
  'scene.noText': 'The server did not serve this scene’s text for this mission. Nothing is played here until the storyline exposes its content.',
  'scene.preparingVoices': 'Preparing the voices of this scene…',
  'missions.hiddenByFilter': '{count} mission(s) hidden by the « {language} » filter.',
  'sp.secretStored': '•••••••• (already stored)',
  // Azure controller by Lighthouse delegation (CCT-327). The `sp.*` keys of the service
  // principal form are gone with the form: the player no longer holds a client, a tenant or a
  // secret, so keeping their wording alive would only invite someone to bring the form back.
  'azure.title': '☁️ Your Azure subscription',
  'azure.help': 'Reviewed in your own Azure subscription, no secret to enter. One click opens the Azure portal with a ready-made deployment granting read-only access (Reader).',
  'azure.connect': 'Connect Azure',
  'azure.retry': 'Retry the check',
  'azure.checking': 'Looking for your deployment on the Azure side…',
  'azure.notSeenYet': 'Nothing visible yet on the Azure side. If the deployment is finished, retry the check; otherwise finish it in the portal, then come back.',
  'azure.reuseExisting': 'A connection is already in progress: use the Azure link you were given, then retry the check.',
  'azure.connectedHelp': 'Your subscription is connected. The controller reads it in read-only mode to review your deployments.',
  'azure.disconnect': 'Disconnect my subscription',
  'azure.cancel': 'Cancel and start over',
  'azure.identityLead': 'is asking for an access on your Azure subscription',
  'azure.identityScope': 'Read-only (Reader role), no right to create, change or delete anything.',
  'azure.identityRevoke': 'You can revoke it at any time from Azure: Service providers > Service provider offers.',
  'azure.armCannotFetch': 'This environment serves the template from a local address. Azure will refuse the deployment (InvalidContentLink).',
  'azure.pickRegion': 'On the portal, choose a Region under Basics before creating: the deployment sits at subscription level and Azure requires one.',
  'azure.identityOpen': 'Continue on the Azure portal',
  'azure.linkLabel': 'Deployment link (if the Azure portal did not open)',
  // What the mission requires BEFORE it is played (CCT-327). The player used to discover it when
  // a reviewed task came back unread, at which point a setup message reads as a verdict.
  'controllers.title': 'To activate for this mission',
  'controllers.subtitle': 'This mission is reviewed on external tools. Activate them now: without them, nothing you deliver can be read.',
  'controllers.ready': 'Ready',
  'prerequisites.title': 'Before you start — prerequisites',
  'prerequisites.p1Unknown': 'Entra ID P1 / P2 required — not verified',
  'prerequisites.p1Help': 'Lighthouse cannot read Entra licences. Check the licence in the tenant linked to your Azure subscription. An active trial is acceptable. A connected subscription does not prove that P1 is active.',
  'prerequisites.checkRequired': 'Prerequisites to check',
  'prerequisites.connectionOnly': 'The Azure indicator verifies the resource connection only. P1 / P2 remains unverified; check it in Microsoft Entra before starting the dynamic group workshop.',
  'controllers.toActivate': 'To activate',
  'controllers.activate': 'Activate',
  'controllers.manage': 'Manage',
  'controllers.cloud': 'Azure subscription',
  'controllers.github': 'GitHub repository',
  'controllers.githubCompany': 'Repository provided by the author',
  'controllers.githubNoMode': 'No repository declared on this mission: ask the author.',
  'controllers.notActivated': 'Not activated yet',
  'controllers.cloudCompany': 'Company subscription',
  'controllers.cloudCompanyBlocked': 'No access to the company subscription yet. The administrators have been notified; it unlocks once they grant it.',
  'controllers.cloudCompanyReady': 'This mission runs in the company subscription. You have nothing to connect: your access was granted by the author.',
  'github.templateTitle': '1 · Create your repository',
  'github.templateBody': 'The author provides a starting repository. Create your own copy: your work stays yours, and nobody else writes in it.',
  'github.templateOpen': 'Create my repository from the template',
  'github.approveTitle': 'Approve read access to your repository',
  'github.approveBody': 'No token is requested. You install the CloudChrono app on the repository of your choice, read-only, and can remove it from GitHub at any time.',
  'github.approve': 'Approve on GitHub',
  'github.openLink': 'Open this link if nothing happened:',
  'github.installed': 'Check the installation',
  'github.grantedFrom': 'Access granted from',
  'github.forget': 'Forget this access',
  'github.forgetHelp': 'CloudChrono stops using it. The app itself lives in YOUR GitHub account: only you can uninstall it.',
  'github.pending': 'Waiting for your approval on GitHub…',
  'github.failed': 'The approval did not complete. Start it again from this mission.',
  'account.missions': 'Missions',
  'account.missionsHelp': 'What you activated for your missions, and how to take it back. Revoking stops CloudChrono from using the access; only you can delete the Azure delegation.',
  'account.revoke': 'Revoke',
  'controllers.blockedTitle': 'Activate the tools first',
  'controllers.blockedOpen': 'Open the mission',
  'controllers.blockedBody': 'Reviewed on external tools that are not connected. Activate them from the mission, or nothing you deliver can be read.',
  'cards.oneCardPerScreen': 'One card per screen. Use Previous/Next to move on and keep a sequential read.',
  'cards.openProject': 'Open the project',
  'payment.totalSpent': 'Total spent',
  'payment.notice': 'These reflect your real payments. Saving cards will become available once a payment provider is integrated.',
  'nav.previous': 'Previous',
  'nav.next': 'Next',
  'us.author.you': 'You',
  'ide.desktopOnly': 'To be done on a desktop machine',
  'dashboard.yourTasks': 'Here are your tasks in progress',
  'chat.thanks': 'Thanks for your answer.',
  'chat.explanation': 'Explanation:',
  'chat.correct': 'Correct!',
  'chat.notQuite': 'Not quite. The right answer: "{answer}"',
  'dashboard.hello': 'Hello, {name}',
  'dashboard.defaultUser': 'User',
  'ide.desktopBody': 'This user story is done in the built-in code editor, available from a desktop browser. Pick the mission back up on cloudchrono.com to open the project.',
  'chapter.characterNoIdentity': '{name} has no identity on the engine side yet: no conversation can be opened for now.',

  // — Why no user story is shown (each branch points somewhere DIFFERENT to repair)
  'usDiag.unknown': 'The content stays playable, but nothing is being recorded. This screen does not know the reason.',
  'usDiag.noneAuthored': 'No user story is written for this mission: there is nothing to track. The task on screen comes from the scenario, not from the engine.',
  'usDiag.noLicense': 'This mission has {count} user story(s), but your learner licence is not active. The engine creates nothing until it is.',
  'usDiag.engineDown': 'This mission has {count} user story(s), but its engine is {state}: it materialises the user stories on every turn, and it is not running.',
  'usDiag.engineStopped': 'stopped ({status})',
  'usDiag.engineNeverStarted': 'never started',
  'usDiag.allGood': 'This mission has {count} user story(s) and its engine is running: yours should appear on the next turn.',

  'common.create': 'Create',
  'common.save': 'Save',
  'common.cancel': 'Cancel',

  'companyDetail.loading': 'Loading the company…',
  'companyDetail.tab.groups': 'Groups',
  'companyDetail.tab.members': 'Members',
  'companyDetail.tab.invitations': 'Invitations',
  'companyDetail.tab.subscription': 'Subscription',
  'companyDetail.tab.licenses': 'Licences',
  'companyDetail.member.roles': 'Roles',
  'companyDetail.member.rolesHelp': 'Given directly to this person. A group can grant more; nothing here takes that away.',
  'companyDetail.member.rolesSaved': 'Roles updated',
  'companyDetail.member.rolesPending': 'Recorded. They will apply when you activate this member.',
  'companyDetail.member.rolesFailed': 'Roles could not be updated.',
  'companyDetail.member.buyLicense': 'Buy a licence',

  'companyDetail.group.name': 'Group name',
  'companyDetail.group.roles': 'Roles',
  'companyDetail.group.members': 'Members',
  'companyDetail.group.missions': 'Granted missions (owned by the company)',
  'companyDetail.group.counts': '{roles} role(s) · {members} member(s)',
  'companyDetail.group.none': 'No group.',
  'companyDetail.member.none': 'No member.',
  'companyDetail.member.admin': 'Administrator',
  'companyDetail.member.activate': 'Activate',
  'companyDetail.member.deactivate': 'Deactivate',
  'companyDetail.member.activateFailed': 'Could not activate.',
  'companyDetail.member.deactivateFailed': 'Could not deactivate.',
  'companyDetail.member.noLicense': 'Not enough licences. Buy licences to activate this member.',
  'companyDetail.license.used': 'Licenses: {used}/{total} in use',
  'companyDetail.license.available': '{count} available',
  'companyDetail.license.inactive': ' · subscription inactive',
  'companyDetail.license.buy': 'Buy licenses →',
  'companyDetail.invite.title': 'Invite a colleague',
  'companyDetail.invite.rolesOnAccept': 'Roles granted on acceptance',
  'companyDetail.invite.groups': 'Groups',
  'companyDetail.invite.requiresAcceptance': 'Requires the guest to accept',
  'companyDetail.invite.requiresAcceptanceOn': 'The guest must accept before joining.',
  'companyDetail.invite.requiresAcceptanceOff': 'By default the member is added and simply notified (no acceptance needed).',
  'companyDetail.invite.submitWithAcceptance': 'Invite (with acceptance)',
  'companyDetail.invite.submitDirect': 'Add and notify',
  'companyDetail.invite.badEmail': 'Invalid email.',
  'companyDetail.invite.sentPending': 'Invitation sent (awaiting acceptance).',
  'companyDetail.invite.added': 'Member added and notified.',
  'companyDetail.invite.sendFailed': 'Could not send.',
  'companyDetail.invite.sent': 'Invitations sent',
  'companyDetail.invite.none': 'No invitation.',
  'companyDetail.invite.resend': 'Resend',
  'companyDetail.invite.resent': 'Invitation resent.',
  'companyDetail.invite.resendFailed': 'Could not resend.',
  'companyDetail.invite.cancel': 'Cancel',
  'companyDetail.invite.cancelled': 'Invitation cancelled.',
  'companyDetail.invite.cancelFailed': 'Could not cancel.',
  'companyDetail.sub.plan': 'Plan',
  'companyDetail.sub.savePlan': 'Save the plan',
  'companyDetail.sub.planSaved': 'Plan saved.',
  'companyDetail.sub.buy': 'Buy licenses',
  'companyDetail.sub.unitPrice': 'Unit price: {price}.',
  'companyDetail.sub.free': 'free',
  'companyDetail.sub.get': 'Get {count} license(s)',
  'companyDetail.sub.pay': 'Pay {total}',
  'companyDetail.sub.added': '{count} license(s) added to your subscription.',
  'companyDetail.sub.paymentLink': 'Open the payment link.',
  'companyDetail.sub.buyFailed': 'Could not complete the purchase.',
  'companyDetail.sub.note': 'Purchased through the cart. A real payment when a provider is configured, simulated otherwise.',

  'invitation.title': 'Invitation',
  'invitation.loading': 'Loading the invitation…',
  'invitation.list.loading': 'Loading invitations…',
  'invitation.list.none': 'No pending invitation.',
  'invitation.list.subtitle': 'Invitation to join the company',
  'invitation.join': 'Join',
  'invitation.joinCompany': 'Join {name}',
  'invitation.decline': 'Decline',
  'invitation.declineInvitation': 'Decline the invitation',
  'invitation.joined': 'You have joined « {name} ». Awaiting activation by an administrator.',
  'invitation.alreadyJoined': 'You have already joined « {name} ».',
  'invitation.declined': 'Invitation to « {name} » declined.',
  'invitation.acceptFailed': 'Could not accept. Check that you are signed in with the invited address.',
  'invitation.declineFailed': 'Could not decline.',
  'invitation.notFound': 'Invitation not found.',
  'invitation.notFoundOrExpired': 'This invitation cannot be found, or it has expired.',
  'invitation.expiredOrCancelled': 'This invitation has expired or was cancelled.',
  'invitation.otherAddress': 'This invitation was sent to a different address. Sign in with the address that received the email.',
  'invitation.switchAccount': 'Switch account',
  'invitation.badLink': 'Invalid invitation link.',
  'invitation.invitedTo': 'You are invited to join ',
  'invitation.signInPrompt': 'You have been invited to subscribe on CloudChrono. Sign in or create an account with the address that received this invitation to accept it.',
  'invitation.signIn': 'Sign in / sign up',
  'invitation.continue': 'Continue',
  'invitation.goHome': 'Go to the home screen',
  'invitation.backHome': 'Back to the home screen',

  // — Account settings
  'settings.title': 'Settings',
  'settings.darkTheme': 'Dark theme',
  'settings.darkThemeSub': 'Toggle between light and dark mode',
  'settings.language': 'Interface language',
  'settings.languageSub': 'The language of buttons, menus and messages',
  'settings.languageLoading': 'Downloading the dictionary…',

  // — Checkout, cart, invoices, card discussion, leaderboard
  //
  // ⚠️ THESE WERE HARD-CODED IN ENGLISH, AND THAT IS WHY THEY SURVIVED EVERY SWEEP: a French-word
  // detector cannot tell an English label from source code. They rendered in English to Spanish,
  // Italian and Arabic speakers with nothing on screen to signal it.
  'checkout.fullName': 'Enter your full name',
  'checkout.email': 'Enter your email',
  'checkout.cardNumber': 'Card number',
  'checkout.nameOnCard': 'Name on card',
  'checkout.secure': 'Payment details are encrypted in transit (SSL).',
  'checkout.thanks': 'Thank you for your purchase. Your missions are now available.',
  'cardChat.placeholder': 'Type your response…',
  'cardChat.youResponded': 'You responded:',
  'cardChat.hint': 'Respond to questions using the options above or the text input',
  'invoices.issuer': 'Mission Platform',
  'invoices.subtitle': 'View and download your purchase history',
  'invoices.search': 'Search invoices…',
  'cart.empty': 'Your cart is empty',
  'cart.emptyHint': 'Browse the marketplace to find missions to add to your cart',
  'missionChat.title': 'Mission chat',
  'missionChat.notFound': 'Mission not found',
  'leaderboard.soon': 'Rankings and competitions coming soon',
  'mission.cancelTrial': 'Cancel trial',
  'mission.notFound': 'Mission not found.',
  // CCT-341 - the AUTHOR's tools. Only shown when the server says this person may manage.
  'mission.manager.title': 'Author tools',
  'mission.manager.help': 'Reload starts the mission over and keeps your controller access. Reset also forgets that access, so you can check the activation screen a learner meets.',
  'mission.manager.reload': 'Reload',
  'mission.manager.reset': 'Reset',
  'mission.manager.confirmReload': 'Start this mission over? Your progress is erased; controller access is kept.',
  'mission.manager.confirmReset': 'Start over and forget controller access? CloudChrono drops its side, but the GitHub App and the Azure delegation stay in your accounts until you remove them.',
  'mission.manager.done': 'Mission reloaded.',
  'notif.markAllRead': 'Mark all read',

  // — Navigation (side menu + account menu). ONE set of keys for both: the same destination
  //   must not be named two different ways depending on the screen size.
  'nav.missions': 'Missions',
  'nav.chat': 'Chat',
  'nav.dashboard': 'Dashboard',
  'nav.leaderboard': 'Leaderboard',
  'nav.invoices': 'Invoices',
  'nav.paymentMethods': 'Payment methods',
  'nav.account': 'Account',
  'nav.company': 'Company workspace',
  'nav.signOut': 'Sign out',
  'nav.lightMode': 'Light mode',
  'nav.darkMode': 'Dark mode',

  // — Account screen
  'profile.title': 'Account',
  'profile.identity': 'MY PLAYER IDENTITY',
  'profile.nameLabel': 'Player name',
  'profile.item.myInfoSub': 'Personal details and preferences',
  'profile.item.dashboardSub': 'View your progress and statistics',
  'profile.item.leaderboardSub': 'Rankings and competitions',
  'profile.item.paymentSub': 'Manage billing and cards',
  'profile.item.invoicesSub': 'Purchase history and receipts',
  'profile.item.companySub': 'Your companies, groups, members and invitations',

  // — Checkout
  'checkout.title': 'Checkout',
  'checkout.address': 'Address',
  'checkout.city': 'City',
  'checkout.country': 'Country',
  'checkout.postalCode': 'Postal code',
  'checkout.emailLabel': 'Email',
  'checkout.fullNameLabel': 'Full name',
  'checkout.expiry': 'Expiry date',
  'checkout.orderSummary': 'Order summary',
  'checkout.subtotal': 'Subtotal',
  'checkout.tax': 'Tax',
  'checkout.total': 'Total',
  'checkout.securePayment': 'Secure payment',
  'checkout.orderConfirmedTitle': 'Order confirmed',
  'checkout.orderNumber': 'Order #',
  'checkout.continue': 'Continue',
  'checkout.continueToMissions': 'Continue to missions',
  'checkout.accessImmediate': '✅ Access available immediately',
  'checkout.missionsAdded': '✅ Missions added to your account',
  'checkout.sampleName': 'John Doe',
  'checkout.sampleStreet': '123 Main Street',
  'checkout.sampleCity': 'New York',
  'checkout.sampleCountry': 'United States',

  // — Invoices
  'invoices.title': 'Invoices',
  'invoices.billTo': 'Bill to:',
  'invoices.download': 'Download',
  'invoices.view': 'View',
  'invoices.filterByStatus': 'Filter by status',
  'invoices.selectStatus': 'Select status filter',
  'invoices.details': 'Invoice details:',
  'invoices.items': 'Items',
  'invoices.subtotal': 'Subtotal:',
  'invoices.tax': 'Tax:',
  'invoices.total': 'Total:',
  'invoices.loadFailed': 'Your invoices could not be loaded.',

  // — Cart
  'cart.title': 'Shopping cart',
  'cart.continueShopping': 'Continue shopping',
  'cart.proceedToCheckout': 'Proceed to checkout',
  'cart.savings': 'Savings',
  'cart.subtotal': 'Subtotal',
  'cart.tax': 'Tax ({rate})',
  'cart.total': 'Total',
  'cart.processing': 'Processing…',

  // — Quiz
  'quiz.start': 'Start',
  'quiz.continue': 'Continue',
  'quiz.correctCount': 'Correct',
  'quiz.explanation': 'Explanation',
  'quiz.restart': 'Start again',
  'quiz.reviewAnswers': 'Review',
  'quiz.requiredScore': 'Required score',
  'quiz.previousAnswersGone': 'Your answers from the previous attempt are no longer available, only the correction. Run the quiz again to compare.',

  // — Home facets and mission actions
  'home.facet.domain': 'Domain',
  'home.facet.topics': 'Topics',
  'home.facet.certifications': 'Certifications',
  'home.facet.languages': 'Languages',
  'home.facet.cloudProvider': 'Cloud provider',
  'home.facet.clear': 'Clear',
  'mission.claimReward': 'Claim reward',
  'mission.open': 'Open',
  'mission.inTrialOpen': 'In trial, open',
  'mission.continue': 'Continue',
  'mission.trialLimitations': 'Trial limitations:',
  'mission.requireLicense': 'require a licence',
  'mission.requireInvitation': 'require an invitation',

  // — Chapter, cards, mission detail
  'chapter.progress': 'Progress',
  'chapter.team': 'Team',
  'chapter.threatLevel': 'THREAT LEVEL HIGH',
  'chapter.abandonOperation': 'ABANDON OPERATION',
  // ⚠️ CE BOUTON N'APPELAIT RIEN — il faisait `goBack()`. Il clôt désormais la partie, ce
  // qui se confirme : le joueur devra la relancer, et ce départ repartira de zéro.
  'chapter.abandonTitle': 'Abandon this mission?',
  'chapter.abandonBody': 'The run is closed. Starting the mission again begins from scratch, and the language is chosen again.',
  'chapter.abandonConfirm': 'Abandon the mission',
  'chapter.abandoning': 'Abandoning…',
  'chapter.abandonFailed': 'The mission could not be abandoned. Nothing has changed, try again.',
  'chapter.requiresPrevious': 'Requires: previous step completed',
  'cards.title': 'Concept cards',
  'cards.card': 'Card',
  'missionDetail.synopsis': 'Synopsis',
  'missionDetail.characters': 'Characters',
  'missionDetail.objectives': 'Learning objectives',
  'missionDetail.continue': 'Continue',

  // — User story / task panels
  'us.comments': 'Comments',
  'us.objective': 'Objective',
  'us.validation': 'Validation',
  'task.effort': 'Effort',
  'task.people': 'People involved',
  'task.objective': 'Objective',
  'task.resources': 'Resources',

  // — My information
  'me.title': 'My information',
  'me.country': 'Country',
  'me.search': 'Search…',
  'me.loadFailed': 'Your details could not be loaded.',

  // — Chats and misc
  'chat.messages': 'Messages',
  'chat.all': 'All',
  // CCT-347 — the message centre lists BY PERSON. Every label below used to be a French literal
  // written straight into the screen: they rendered untranslated in the four other languages, and
  // the plural was decided by a `> 1 ? 's' : ''` that no locale can be expected to follow.
  'chat.conversationOne': '1 conversation',
  'chat.conversationMany': '{count} conversations',
  'chat.questionOne': '1 question',
  'chat.questionMany': '{count} questions',
  'chat.missionN': 'Mission {id}',
  'chat.character': 'Character',
  'chat.tone.default': 'Discussion',
  'chat.openCount': '{count} in progress',
  'missionChat.freeText': 'Answer in your own words:',
  'missionChat.yourAnswers': 'Your answers:',
  'missionChat.questionsAnswered': 'questions answered',
  // CCT-471 — a scored chat. The threshold is named in every case: a score shown without the bar
  // it is measured against tells the player nothing about why they may or may not move on.
  'missionChat.scoreSending': 'Sending your result…',
  'missionChat.scoreFailed': 'Result not sent, so this conversation stays open. Reopen it to try again.',
  'missionChat.scorePassed': 'Score {score}%, threshold of {threshold}% reached',
  'missionChat.scoreMissed': 'Score {score}%, threshold of {threshold}% not reached',
  'missionChat.scoreNothingToGrade': 'This conversation has nothing to grade: no question has a right answer.',
  'cardChat.responses': 'Responses:',
  'cardChat.submit': 'Submit response',
  // GitHub controller (CCT-305). `sp.secretStored` is REUSED for the "already stored" placeholder:
  // the same fact deserves one wording, and a second key would have drifted from the first.
  'auth.continue': 'Continue',
  'auth.or': 'or',
  'auth.changeEmail': '← Change email',
  'company.studio': 'Studio',
  'payment.last': 'Latest',
  'payment.payments': 'Payments',
  'common.confirm': 'Confirm',
  'common.syncing': 'Syncing…',
  'common.all': 'All',
  'common.completed': 'Completed',
  'common.inProgress': 'In progress',
  'chapter.panel.progression': 'PROGRESSION',
  'chapter.panel.team': 'TEAM',
  'launch.title': "Choose the language",
  'launch.body': "The language is chosen now and cannot be changed afterwards. To play this story in another language, start the mission again.",
  'launch.confirm': "Start",
  'launch.unavailable': 'The mission could not be started. Your progress is unchanged. Please try again.',
  'chapter.loadFailed': 'The chapter could not be loaded. Please try again.',
  'launch.failed': "The mission could not be started. You can still read it, but nothing will be recorded.",
  'launch.preparing': 'Preparing the mission…',
  'launch.starting': "Starting the mission…",
  // ⚠️ ON ANNONCE L'EFFACEMENT AVANT, PAS APRÈS. Une partie jamais démarrée peut porter
  // de la progression : elle a été jouée sans qu'aucune langue soit choisie, donc elle
  // n'appartient à aucune partie. Démarrer repart de zéro.
  'launch.freshStart': "{count} item(s) were already played on this mission without a"
    + " language ever being chosen. Starting begins a fresh run and clears them.",
  'chapter.tab.timeline': 'Timeline',
  'chapter.tab.team': 'Team',
  'chapter.tab.progress': 'Progress',
  'cards.section': 'Card',
  'missionChat.exchanges': 'exchanges',


  // - Messages BUILT BY THE SERVER (CCT-400). The runtime sends the KEY; these are the

  // English source of record, and `tests/test_server_messages.py` on the runtime side

  // fails if the two texts drift apart.

  'srv.mission.needsInvite': 'You must be invited by the company to start this mission.',

  'srv.mission.needsLicence': 'An active licence is required to start this mission.',

  'srv.mission.reloadForbidden': 'Only the mission author or an administrator can reload it.',

  'srv.mission.noAccess': 'You do not have access to this mission.',

  'srv.controllers.required': 'Reviewed on external tools that are not connected. Activate them from the mission, or nothing you deliver can be read.',

  'srv.controllers.githubUndefined':

    'No GitHub controller is referenced by this mission. The author must choose ' +

    'one in the builder.',

  'srv.azure.connectionNotFound': 'Connection not found.',

  'srv.azure.linkExpired': 'The connection link has expired. Start Connect Azure again.',

  'srv.azure.wrongSubscription': 'The delegation does not cover the expected subscription.',

  'srv.azure.notActiveYet':

    'Azure received the deployment; the access is not active on its side yet. ' +

    'This usually settles on its own, try again shortly.',

  'srv.azure.alreadyBound':

    'This subscription is already attached to another CloudChrono account.',

  'srv.azure.notVisibleYet': 'Deployment not visible on the Azure side yet.',

  'srv.github.notConfigured':

    'The GitHub controller is not configured: App ID, short name or private key ' +

    'is missing under Content, Controllers.',

  'srv.github.linkExpired':

    'This installation link is no longer valid. Start the approval again from ' +

    'the mission.',

  'srv.github.controllerGone':

    'The GitHub controller is no longer configured, so the repository cannot be ' +

    'attached.',

  'srv.github.unreachable': 'GitHub is unreachable. Try again shortly.',

  'srv.github.noAccess':

    'No GitHub access granted. Install the app on your repository from the ' +

    'mission, then send the task back for review.',

  'srv.github.tokenRenewFailed':

    'The GitHub access could not be renewed. Check it from the mission; your ' +

    'work is not in question.',

  'srv.github.noRepository':

    'The app has access to no repository. Choose one in GitHub, then send the ' +

    'task back for review.',

  'srv.github.installNotFound': 'Installation not found.',

  'srv.github.forgotten':

    'CloudChrono has forgotten this installation. To remove the access ' +

    'entirely, uninstall the app from GitHub, where it lives.',

  'srv.github.repoNotFromTemplate': 'The repository {repo} does not come from the mission\'s starting repository ({template}). Create it from the mission.',

  'srv.github.repoAmbiguous':
    'Several repositories are accessible ({repos}) and the task names none of t' +
    'hem. The author must say which one to review.',

  'srv.us.missionNotAbandoned': 'This mission is not abandoned.',

  'srv.us.missionClosedAfterAbandon': 'The author closed this mission after it was abandoned.',

  'srv.us.chatClosed': 'This conversation is over.',

  'srv.us.answerAlreadyUsed': 'You have already asked this question on this task.',

  'srv.us.repoUnreadable':

    'Your repository could not be analysed for now. Send the task back for ' +

    'review shortly; your work is not in question.',

  'srv.us.reviewPending': 'Your delivery is being analysed.',
  'srv.us.readyRequired': 'Declare this task Ready; it has no automatic inspection.',

  'srv.us.reviewPendingJournal':

    'Your delivery is being analysed. The Azure activity log takes a minute or two to ' +

    'reflect your latest actions.',

  'srv.project.solutionLocked': 'The solution opens once the task is validated or abandoned.',

} as const;

/** Every message key. Adding one to `en` makes it required everywhere else. */
export type MessageKey = keyof typeof en;

/** A resolved dictionary: what `translate` looks a key up in. */
export type Messages = Partial<Record<MessageKey, string>>;

/** The English dictionary, as data. This is what the app boots with, before any network call. */
export const BUNDLED: Messages = en;

// ---------------------------------------------------------------------------
// Languages
// ---------------------------------------------------------------------------
//
// ⚠️ THIS IS NOT THE CATALOGUE FILTER. Two different things share the word "language" here, and
// confusing them has already produced a defect (CCT-279: the home screen hid 13 missions out of
// 14 because a content default was applied as a filter):
//
//   * the UI LANGUAGE — resolved below. It decides which labels are rendered. English by default.
//   * the PREFERRED CONTENT LANGUAGE (`usePreferredLanguage`) — the player's catalogue filter.
//     Its empty value means "every language", and it must never fall back to English.
//
// They are kept apart on purpose: someone browsing the interface in English may well want to see
// missions in every language.

/** The source language. Also the fallback when nothing else resolves. */
export const DEFAULT_UI_LANGUAGE = 'en';

/** Languages the product offers. The server is authoritative; this is the offline answer. */
export const UI_LANGUAGES = ['en', 'fr', 'es', 'it', 'ar'] as const;

/** Languages whose script runs right to left — a LAYOUT concern, not only a text one. */
export const RTL_LANGUAGES = ['ar'] as const;

/** Human names, each in its OWN language: a picker that names languages in a language you cannot
 *  read is useless to the very person who needs it. */
export const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English', fr: 'Français', es: 'Español', it: 'Italiano', ar: 'العربية',
};

/** `FR-fr `, `fr_FR` -> `fr`. Only the BASE language selects a dictionary. */
export function baseLanguage(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/_/g, '-').split('-')[0] ?? '';
}

/** Does this language read right to left? */
export function isRtl(language: unknown): boolean {
  return (RTL_LANGUAGES as readonly string[]).includes(baseLanguage(language));
}

/**
 * Which UI language to use, from an explicit parameter and a stored preference.
 *
 * ⚠️ NO AUTOMATIC DEVICE-LOCALE DETECTION. English is the default, deliberately: a product that
 * silently followed the device would show a French UI to an English speaker whose laptop happens
 * to be configured in French, with no obvious way back. The language changes when someone ASKS.
 *
 * ⚠️ AN UNKNOWN LANGUAGE FALLS BACK, IT DOES NOT BLANK. Asking for `?lang=de` with no German
 * dictionary must render English, not an app full of raw keys.
 */
export function resolveUiLanguage(param?: unknown, stored?: unknown): string {
  for (const candidate of [param, stored]) {
    const b = baseLanguage(candidate);
    if (b && (UI_LANGUAGES as readonly string[]).includes(b)) return b;
  }
  return DEFAULT_UI_LANGUAGE;
}

/**
 * Reads the `lang` parameter from the current URL, if there is one.
 *
 * This is the "language as a parameter" entry point: `?lang=fr` switches the interface without
 * touching any stored state — which makes a translation reviewable by simply sharing a link.
 * Returns `null` outside a browser (native), where there is no URL to read.
 */
export function uiLanguageFromUrl(search?: string): string | null {
  try {
    const raw = search ?? (typeof window !== 'undefined' ? window.location.search : '');
    if (!raw) return null;
    return new URLSearchParams(raw).get('lang');
  } catch {
    // A malformed URL must not prevent the app from rendering.
    return null;
  }
}

/** Replaces `{name}` placeholders. A value that is not supplied leaves the placeholder in place
 *  rather than printing `undefined` — a visible gap is easier to spot than a lie. */
function interpolate(template: string, values?: Record<string, unknown>): string {
  if (!values) return template;
  return template.replace(/\{(\w+)\}/g, (whole, key) =>
    (key in values ? String(values[key]) : whole));
}

/**
 * Looks a message up in a dictionary, with an explicit fallback chain.
 *
 * ⚠️ NEVER RENDERS EMPTY. A key missing from the downloaded dictionary falls back to the bundled
 * English, then to the key itself. An empty label is the worst outcome: the screen looks broken
 * and nothing says why, whereas a visible `home.tab.trial` says exactly what is missing.
 *
 * ⚠️ IT TAKES A DICTIONARY, NOT A LANGUAGE CODE. The previous signature took a code and looked it
 * up in a bundled map; there is no such map any more — the active dictionary is STATE, held by
 * the store. Passing a code would mean this module reaching into that state, and a pure lookup
 * would become something that answers differently depending on when it is called.
 */
export function translate(messages: Messages | null | undefined, key: MessageKey,
                          values?: Record<string, unknown>): string {
  const template = messages?.[key] ?? en[key] ?? key;
  return interpolate(template, values);
}
